import * as React from 'react'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'
import { padToX, strictParseInt } from '../../../shared/lib/number.no-deps'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { TimelineDetails } from './TimelineDetails'
import { oneMinuteInMs } from '../../../shared/lib/duration.no-deps'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'

import {
    getDateComponents,
    getTimeComponents,
    answerValueToDate,
    isValidDateComponents,
    isValidTimeComponents,
} from '../../../shared/domain/flows/dateTime.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    selectType: 'date' | 'time' | 'datetime'
    initialValue: Flows.DateTimeAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.DateTimeAnswer>
    reportStartDate: Date
    openOptions?: () => void
}

interface State {
    currentValue: Flows.DateTimeAnswer['value']
}

export class ElementDateTime extends React.Component<Props, State> {
    public state: State = { currentValue: this.props.initialValue }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private onDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const [year, month, day] = event.target.value.split('-').map(strictParseInt)
        const isValid = isValidDateComponents({ year, month, day })
        const components = isValid ? { year, month, day } : getDateComponents(this.props.reportStartDate)
        this.updateAnswer({ date: components })
    }

    private onTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const [hour, minute] = event.target.value.split(':').map(strictParseInt)
        const isValid = isValidTimeComponents({ hour, minute })
        const components = isValid ? { hour, minute } : getTimeComponents(this.props.reportStartDate)
        this.updateAnswer({ time: components })
    }

    private updateAnswer(components: { date: Flows.DateComponents } | { time: Flows.TimeComponents }) {
        // Can get the timezone offset of the device from any date object
        const timezoneOffsetInMs = this.props.reportStartDate.getTimezoneOffset() * oneMinuteInMs
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

        const newAnswer = { ...this.props.initialValue, ...components, timezoneOffsetInMs, timezone }

        this.setState({ currentValue: newAnswer })

        this.props.createAnswer((base) => ({
            ...base,
            type: 'datetime',
            value: newAnswer,
        }))
    }

    private renderDateInput = (date: Date) => {
        const { selectType } = this.props
        const value = this.state.currentValue

        if (selectType === 'time') return null
        if (value.type === 'time') return null

        const { year, month, day } = getDateComponents(date)

        // This is the format that the date input uses
        const dateString = `${padToX(year, 4)}-${padToX(month, 2)}-${padToX(day, 2)}`

        return <input type="date" value={dateString} onChange={this.onDateChange} />
    }

    private renderTimeInput = (date: Date) => {
        const { selectType } = this.props
        const value = this.state.currentValue

        if (selectType === 'date') return null
        if (value.type === 'date') return null

        const { hour, minute } = getTimeComponents(date)

        // This is the format the time input uses
        const timeString = `${padToX(hour, 2)}:${padToX(minute, 2)}`

        return <input type="time" value={timeString} onChange={this.onTimeChange} />
    }

    private tryRender = () => {
        const { deps, elementId, label, timelineDetails, required, validationMessages } = this.props
        const date = answerValueToDate(this.state.currentValue, this.props.reportStartDate)

        return (
            <ExecutionElement
                deps={deps}
                elementId={elementId}
                loopPathIds={this.props.loopPathIds}
                validationMessages={validationMessages}
                timelineDetails={timelineDetails}
            >
                <ElementLabel
                    required={required}
                    label={label}
                    hasBeenEdited={timelineDetails && timelineDetails.hasBeenEdited}
                    language={deps.language}
                    validationMessages={validationMessages}
                    openOptions={this.props.openOptions}
                />
                <div className="AppCore__flow__DateTime">
                    <div className="AppCore__flow__DateTime__input">
                        {this.renderDateInput(date)}
                        {this.renderTimeInput(date)}
                    </div>
                </div>
            </ExecutionElement>
        )
    }
}
